import * as React from "react"
import { Head, Link, router } from "@inertiajs/react"
import { BarChart3, FileText, Plus, Search } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import {
  type AccountOption,
  directiveStatusLabel,
  directiveStatusTone,
  type DirectiveSummary,
  directiveTypeLabel,
  formatDate,
} from "@/lib/accountDirectives"

type Filters = {
  account_id: string | null
  directive_type: string | null
  status: string | null
  search: string | null
  sort_by: string
  sort_order: string
}

type PageProps = {
  directives: DirectiveSummary[]
  pagination: { total_count: number; page: number; per_page: number }
  filters: Filters
  filter_options: {
    accounts: AccountOption[]
    directive_types: string[]
    statuses: string[]
  }
}

const DESCRIPTION =
  "Ledger arahan akun→produk (peringatan): memo per-akun dengan baris SKU, metrik, tenggat masa tenggang, dan dokumen sumber."

export default function Index({ directives, pagination, filters, filter_options }: PageProps) {
  const [search, setSearch] = React.useState(filters.search ?? "")

  const applyFilters = (next: Partial<Filters> & { page?: string }) => {
    const merged = {
      account_id: filters.account_id ?? "",
      directive_type: filters.directive_type ?? "",
      status: filters.status ?? "",
      search,
      sort_by: filters.sort_by,
      sort_order: filters.sort_order,
      ...next,
    }
    const query: Record<string, string> = {}
    Object.entries(merged).forEach(([k, v]) => {
      if (v != null && v !== "") query[k] = String(v)
    })
    router.get("/admin/accounts/directives", query, {
      preserveState: true,
      preserveScroll: true,
      replace: true,
    })
  }

  const onSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    applyFilters({ search })
  }

  const totalPages = Math.max(1, Math.ceil(pagination.total_count / pagination.per_page))

  return (
    <>
      <Head title="Peringatan Produk">
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content="Peringatan Produk" />
        <meta property="og:description" content={DESCRIPTION} />
      </Head>
      <AppShell>
        <div className="mb-2 flex items-center gap-1.5 text-xs text-ink-muted">
          <span>Outlet</span>
          <span>/</span>
          <Link href="/admin/accounts" className="hover:text-ink-body">
            Account
          </Link>
          <span>/</span>
          <span className="text-ink-body font-medium">Peringatan</span>
        </div>

        <div className="border-b border-hairline pb-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1>Peringatan Produk</h1>
              <p className="mt-1 text-sm text-ink-muted">{DESCRIPTION}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button asChild variant="secondary">
                <Link href="/admin/accounts/directives/analytics">
                  <BarChart3 className="mr-2 h-4 w-4" />
                  Analitik
                </Link>
              </Button>
              <Button asChild>
                <Link href="/admin/accounts/directives/new">
                  <Plus className="mr-2 h-4 w-4" />
                  Tambah Peringatan
                </Link>
              </Button>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
          <form onSubmit={onSearchSubmit} className="flex-1 min-w-[200px]">
            <label htmlFor="search">Cari</label>
            <div className="relative mt-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" />
              <Input
                id="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="No. referensi atau brand"
                className="pl-9"
              />
            </div>
          </form>
          <div className="min-w-[180px]">
            <label htmlFor="account_id">Akun</label>
            <Select
              id="account_id"
              className="mt-1"
              value={filters.account_id ?? ""}
              onChange={(e) => applyFilters({ account_id: e.target.value })}
            >
              <option value="">Semua akun</option>
              {filter_options.accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </Select>
          </div>
          <div className="min-w-[150px]">
            <label htmlFor="status">Status</label>
            <Select
              id="status"
              className="mt-1"
              value={filters.status ?? ""}
              onChange={(e) => applyFilters({ status: e.target.value })}
            >
              <option value="">Semua status</option>
              {filter_options.statuses.map((s) => (
                <option key={s} value={s}>
                  {directiveStatusLabel(s)}
                </option>
              ))}
            </Select>
          </div>
          <div className="min-w-[150px]">
            <label htmlFor="sort_by">Urut</label>
            <Select
              id="sort_by"
              className="mt-1"
              value={filters.sort_by}
              onChange={(e) => applyFilters({ sort_by: e.target.value })}
            >
              <option value="issued_on">Tanggal terbit</option>
              <option value="grace_deadline">Tenggat tenggang</option>
              <option value="created_at">Dibuat</option>
            </Select>
          </div>
        </div>

        {/* List */}
        {directives.length === 0 ? (
          <div className="mt-10 rounded-lg border border-hairline bg-surface p-10 text-center">
            <p className="text-ink-muted">
              Belum ada peringatan. Klik <strong>Tambah Peringatan</strong> untuk mencatat memo
              warning dari sebuah akun.
            </p>
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="mt-6 hidden overflow-x-auto rounded-lg border border-hairline md:block">
              <table className="w-full text-sm">
                <thead className="bg-surface text-left text-ink-muted">
                  <tr>
                    <th className="px-4 py-3 font-medium">No. Ref / Brand</th>
                    <th className="px-4 py-3 font-medium">Akun</th>
                    <th className="px-4 py-3 font-medium">Terbit</th>
                    <th className="px-4 py-3 font-medium">Tenggat terdekat</th>
                    <th className="px-4 py-3 font-medium text-right">SKU</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {directives.map((d) => (
                    <tr key={d.id} className="border-t border-hairline hover:bg-surface">
                      <td className="px-4 py-3">
                        <Link
                          href={`/admin/accounts/directives/${d.id}`}
                          className="font-medium inline-flex items-center gap-1.5"
                        >
                          {d.has_document && <FileText className="h-3.5 w-3.5 text-ink-muted" />}
                          {d.ref_no ?? "Tanpa nomor"}
                        </Link>
                        {d.brand && <div className="text-xs text-ink-muted">{d.brand}</div>}
                      </td>
                      <td className="px-4 py-3 text-ink-body">{d.account_name ?? "—"}</td>
                      <td className="px-4 py-3 text-ink-muted">{formatDate(d.issued_on)}</td>
                      <td className="px-4 py-3 text-ink-muted">{formatDate(d.nearest_deadline)}</td>
                      <td className="px-4 py-3 text-right text-ink-body">{d.line_count}</td>
                      <td className="px-4 py-3">
                        <Badge tone={directiveStatusTone(d.status)}>
                          {directiveStatusLabel(d.status)}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="mt-6 flex flex-col gap-3 md:hidden">
              {directives.map((d) => (
                <Link
                  key={d.id}
                  href={`/admin/accounts/directives/${d.id}`}
                  className="rounded-lg border border-hairline bg-surface p-4"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-medium text-ink-body inline-flex items-center gap-1.5">
                        {d.has_document && <FileText className="h-3.5 w-3.5 text-ink-muted" />}
                        {d.ref_no ?? "Tanpa nomor"}
                      </div>
                      <div className="text-xs text-ink-muted">{d.account_name}</div>
                    </div>
                    <Badge tone={directiveStatusTone(d.status)}>
                      {directiveStatusLabel(d.status)}
                    </Badge>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    <Badge tone="neutral">{directiveTypeLabel(d.directive_type)}</Badge>
                    {d.brand && <Badge tone="muted">{d.brand}</Badge>}
                    <Badge tone="muted">{d.line_count} SKU</Badge>
                  </div>
                  <div className="mt-2 text-xs text-ink-muted">
                    Terbit {formatDate(d.issued_on)} · Tenggat {formatDate(d.nearest_deadline)}
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between text-sm text-ink-muted">
            <span>
              Halaman {pagination.page} dari {totalPages} ({pagination.total_count} peringatan)
            </span>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={pagination.page <= 1}
                onClick={() => applyFilters({ page: String(pagination.page - 1) })}
              >
                Sebelumnya
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={pagination.page >= totalPages}
                onClick={() => applyFilters({ page: String(pagination.page + 1) })}
              >
                Berikutnya
              </Button>
            </div>
          </div>
        )}
      </AppShell>
    </>
  )
}
